import api from './api';
import {
  BookingAvailabilityRequest,
  BookingAvailabilityResponse,
  FacilityType,
  HittingBayAvailabilityEntry,
} from '@/types/booking.types';

export interface CreateBookingRequest {
  locationId: string;
  facility: string;
  teamMemberId?: string;
  roomId?: string;
  bookingDate: string;
  calendarId?: string;
  startTime: string;
  endTime: string;
  timezone?: string;
  paymentId?: string;
  /** Create the booking before the card is charged — see BookingService.confirmBookingPayment. */
  deferPayment?: boolean;
  isReschedule?: boolean;
  rescheduleBookingId?: string;
  alternateCalendarIds?: string[];
  /** Ask for a 15-minute Box Pro check-in during this booking. */
  requestBoxProCheckIn?: boolean;
  /** Only when more than one Box Pro is on duty and the member chose one. */
  boxProId?: string;
  /** What the member wants the check-in for. */
  boxProRequestNote?: string;
}

export type BoxProCheckInStatus = 'awaited' | 'confirmed' | 'declined' | 'not_available';

/** Box Pro Confirmation on a booking. The Box Pro picks the time on accept. */
export interface BoxProCheckIn {
  status: BoxProCheckInStatus;
  boxProId?: string;
  boxProName?: string;
  slotStartTime?: string;
  slotEndTime?: string;
  /** The Box Pro's note to the member. */
  note?: string;
  /** The member's reason, sent with the request. */
  requestNote?: string;
  requestedAt: string;
  respondedAt?: string;
}

export interface BoxProCheckInEligibilityRequest {
  locationId?: string;
  facility: string;
  /** mm-dd-YYYY, like the booking request */
  bookingDate: string;
  startTime: string;
  endTime: string;
  rescheduleBookingId?: string;
  /** Existing booking (session details): Box Pros who declined it are left out. */
  bookingId?: string;
}

export interface BoxProCheckInEligibility {
  /** False hides the toggle: the plan or location doesn't include it. */
  offered: boolean;
  available: boolean;
  unavailableReason?: 'NO_BOX_PRO_ON_DUTY' | 'WEEKLY_LIMIT_REACHED' | 'OUTSIDE_WORKING_HOURS';
  boxPros: { id: string; name: string }[];
  weeklyLimit: number;
  remainingThisWeek: number;
  /** yyyy-MM-dd the weekly allowance resets */
  nextAvailableDate?: string;
  /** Each Box Pro's hours (HH:mm, location time) on the booking date. */
  dayHours?: {
    boxProId: string;
    name: string;
    intervals: { from: string; to: string }[];
    /** Only on a day off: the next day (yyyy-MM-dd) with hours. */
    nextWorkingDay?: { date: string; intervals: { from: string; to: string }[] };
  }[];
}

export interface CreateBookingResponse {
  id: string;
  status: string;
  /** `pending` when the server is holding this booking for a charge that
   *  has not landed yet. Absent when nothing is owed — the server, not
   *  the client, decides whether the card is taken. */
  paymentStatus?: 'pending' | 'paid';
  paymentAmount?: number;
  boxProCheckIn?: BoxProCheckIn;
  // Add other response fields as needed
}

export interface LocationDetails {
  id: string;
  name: string;
  address: string;
  geoLocation?: {
    latitude: number;
    longitude: number;
  };
  /** Resolved Box Pro contact for the check-in "Contact us" link. */
  boxProContact?: {
    type: 'tel' | 'mailto';
    value: string;
  };
}

export interface BookingDetailsDto {
  id: string; // MongoDB _id serialised as 'id' per API contract
  _id?: string; // deprecated — kept optional so legacy code compiles; prefer id
  locationId: string;
  memberId: string;
  facility: string;
  bookingDate: string;
  startDate: string;
  startTime: string;
  endTime: string;
  calendarId: string;
  title: string;
  bookingId: string;
  status: string;
  timezone?: string;
  location?: LocationDetails;
  assignedUser?: { id: string; crmUserId: string; firstName: string; lastName: string };
  assignedRooms?: { id: string; crmId: string; name: string; resourceType: string }[];
  isFree?: boolean;
  snapshotSponsorName?: string;
  /** Minutes before startTime that check-in opens, resolved per facility
   *  by the API (30 for bays, 5 for the short-turnaround rooms). */
  checkInWindowMinutes?: number;
  boxProCheckIn?: BoxProCheckIn;
}

/**
 * An `Error` that still knows where it came from.
 *
 * The catch blocks below flatten an Axios failure into a plain
 * `new Error(message)`. That is right for the message — callers get
 * the server's sentence on `.message` without unwrapping anything,
 * and several already rely on it — but it also threw away the only
 * evidence of whether the sentence WAS the server's. Once
 * `error.response` is gone, "You have reached the limit of 1 active
 * reservation(s) for this facility." and the local
 * "Failed to get booking availability" fallback are indistinguishable
 * strings, so a screen cannot show one and suppress the other.
 *
 * Re-attaching `response` costs nothing, changes no existing
 * behaviour — `.message` is byte-for-byte what it was — and lets
 * `getApiMessage()` in @/utils/error tell the two apart.
 */
const asServiceError = (error: any, fallback: string): Error => {
  const message =
    error?.response?.data?.message || error?.response?.data?.error || fallback;
  const wrapped = new Error(message);
  (wrapped as any).response = error?.response;
  return wrapped;
};

export class BookingService {
  /**
   * Get booking availability for a specific facility and date
   */
  static async getAvailability(
    request: BookingAvailabilityRequest
  ): Promise<BookingAvailabilityResponse> {
    try {
      const response = await api.post('/bookings/get-availability', request);
      return response.data;
    } catch (error: any) {
      throw asServiceError(error, 'Failed to get booking availability');
    }
  }

  /**
   * Ask whether the member may book this facility on this date — the same
   * gate get-availability runs before it returns slots, without the slots.
   * Resolves when they may; rejects with the server's reason (e.g. "We
   * couldn't find an active membership on your account.") when they may not.
   */
  static async checkEligibility(
    locationId: string,
    facility: FacilityType,
    bookingDate: string,
    rescheduleBookingId?: string
  ): Promise<{ allowed: true }> {
    try {
      const response = await api.post('/bookings/eligibility', {
        locationId,
        facility,
        bookingDate,
        ...(rescheduleBookingId ? { isReschedule: true, rescheduleBookingId } : {}),
      });
      return response.data;
    } catch (error: any) {
      throw asServiceError(error, 'Failed to check booking eligibility');
    }
  }

  /**
   * Get aggregated hitting bay availability for a location and start date
   */
  static async getHittingBayAvailability(
    locationId: string,
    startDate: string
  ): Promise<HittingBayAvailabilityEntry[]> {
    try {
      const response = await api.get('/bookings/hitting-bay/availability', {
        params: { locationId, startDate },
      });
      return response.data;
    } catch (error: any) {
      throw asServiceError(error, 'Failed to get hitting bay availability');
    }
  }

  /**
   * Get current time in a specific timezone in mm-dd-yyyy HH:MM:SS format
   */
  static getCurrentTimeInTimezone(timezone?: string): string {
    const now = new Date();

    if (!timezone) {
      // Format as mm-dd-yyyy HH:MM:SS
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const year = now.getFullYear();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}`;
    }

    // Get date in target timezone
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: timezone,
    };

    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(now);

    const month = parts.find(p => p.type === 'month')?.value || '';
    const day = parts.find(p => p.type === 'day')?.value || '';
    const year = parts.find(p => p.type === 'year')?.value || '';
    const hour = parts.find(p => p.type === 'hour')?.value || '';
    const minute = parts.find(p => p.type === 'minute')?.value || '';
    const second = parts.find(p => p.type === 'second')?.value || '';

    return `${month}-${day}-${year} ${hour}:${minute}:${second}`;
  }

  /**
   * Get booking availability without coach (initial call)
   */
  static async getAvailabilityForFacility(
    locationId: string,
    facility: FacilityType,
    bookingDate: string,
    timezone?: string,
    rescheduleBookingId?: string,
    roomId?: string
  ): Promise<BookingAvailabilityResponse> {
    return this.getAvailability({
      locationId,
      facility,
      bookingDate,
      timezone,
      currentTime: this.getCurrentTimeInTimezone(timezone),
      ...(roomId ? { roomId } : {}),
      ...(rescheduleBookingId ? { isReschedule: true, rescheduleBookingId } : {}),
    });
  }

  /**
   * Get booking availability with specific coach
   */
  static async getAvailabilityWithCoach(
    locationId: string,
    facility: FacilityType,
    teamMemberId: string,
    bookingDate: string,
    timezone?: string,
    rescheduleBookingId?: string,
    roomId?: string
  ): Promise<BookingAvailabilityResponse> {
    return this.getAvailability({
      locationId,
      facility,
      teamMemberId,
      bookingDate,
      timezone,
      currentTime: this.getCurrentTimeInTimezone(timezone),
      ...(roomId ? { roomId } : {}),
      ...(rescheduleBookingId ? { isReschedule: true, rescheduleBookingId } : {}),
    });
  }

  /**
   * Get which upcoming dates (starting from bookingDate) have any available
   * slot at all — lets the date picker disable/skip dead days instead of
   * letting the member pick one and discover it's empty.
   */
  static async getAvailableDates(
    locationId: string,
    facility: FacilityType,
    bookingDate: string,
    timezone?: string,
    daysAhead?: number,
    teamMemberId?: string,
    roomId?: string,
    rescheduleBookingId?: string
  ): Promise<Record<string, boolean>> {
    try {
      const response = await api.post('/bookings/available-dates', {
        locationId,
        facility,
        bookingDate,
        timezone,
        ...(daysAhead ? { daysAhead } : {}),
        ...(teamMemberId ? { teamMemberId } : {}),
        ...(roomId ? { roomId } : {}),
        /* Same discount getAvailabilityForFacility applies. Without it this
           endpoint runs the once-at-a-time gates against the very booking
           being moved: a Golf Performance Assessment reschedule 400s with "You already
           have an upcoming Golf Performance Assessment booking", the date strip gets an
           error instead of a map, and every day reads as dead. */
        ...(rescheduleBookingId ? { isReschedule: true, rescheduleBookingId } : {}),
      });
      return response.data;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to get available dates';
      throw new Error(message);
    }
  }

  /**
   * Create a new booking
   */
  static async createBooking(
    request: CreateBookingRequest
  ): Promise<CreateBookingResponse> {
    try {
      const response = await api.post('/bookings/add-booking', request);
      return response.data;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to create booking';
      throw new Error(message);
    }
  }

  /**
   * Attach a settled charge to a booking created with `deferPayment`.
   *
   * Paired with `cancelUnpaidBooking`: a paid drop-in is booked first and
   * charged second, so every deferred booking ends at exactly one of these
   * two calls.
   */
  static async confirmBookingPayment(
    bookingId: string,
    paymentId: string
  ): Promise<BookingDetailsDto> {
    try {
      const response = await api.post(`/bookings/${bookingId}/confirm-payment`, {
        paymentId,
      });
      return response.data;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Payment could not be applied to your booking';
      throw new Error(message);
    }
  }

  /**
   * Release a booking whose payment failed or was abandoned.
   *
   * Never surfaced to the member as an action — it is the rollback half of
   * the book-first flow, so it swallows its own failure rather than
   * replacing the payment error the member actually needs to read.
   */
  static async cancelUnpaidBooking(bookingId: string): Promise<void> {
    await api.post(`/bookings/${bookingId}/cancel-unpaid`);
  }

  /**
   * Get all bookings for dashboard (no tab filtering)
   */
  static async getAllBookingsForDashboard(): Promise<BookingDetailsDto[]> {
    try {
      const response = await api.get('/bookings/getAll', { params: { limit: 5, status: 'active,checkedIn' } });
      return response.data;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to get bookings';
      throw new Error(message);
    }
  }

  /**
   * Sessions booked and not cancelled, counted server-side.
   *
   * Not from `membershipUsage.bookingCount`: that tally only ever counted
   * REGULAR bookings (so drop-ins were invisible) and resets each billing
   * period, which is not what "sessions booked" means on the profile.
   */
  static async getSessionsBooked(): Promise<number> {
    const response = await api.get('/bookings/sessions-booked');
    return response.data?.bookedCount ?? 0;
  }

  /**
   * Get paginated bookings for the logged-in user
   */
  static async getAllBookings(
    tab: 'upcoming' | 'past' | 'canceled' = 'upcoming',
    page = 1,
    pageSize = 10
  ): Promise<{
    data: BookingDetailsDto[];
    total: number;
    /** Past tab only: sessions attended across the whole history, no-shows excluded. */
    completedTotal?: number;
    /** Minutes those sessions added up to — same set as `completedTotal`. */
    completedMinutes?: number;
    totalPages: number;
    page: number;
  }> {
    try {
      const response = await api.post('/bookings/list', { tab, page, pageSize });
      return {
        data: response.data?.data || [],
        total: response.data?.total ?? 0,
        completedTotal: response.data?.completedTotal,
        completedMinutes: response.data?.completedMinutes,
        totalPages: response.data?.totalPages ?? 1,
        page: response.data?.page ?? page,
      };
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to get bookings';
      throw new Error(message);
    }
  }

  /** Whether the Box Pro check-in toggle is hidden, greyed out or available. */
  static async getBoxProCheckInEligibility(
    request: BoxProCheckInEligibilityRequest
  ): Promise<BoxProCheckInEligibility> {
    const response = await api.post('/box-pro-check-ins/eligibility', request);
    return response.data;
  }

  /** Turn the Box Pro check-in on for an existing booking. */
  static async requestBoxProCheckIn(
    bookingId: string,
    boxProId?: string,
    requestNote?: string
  ): Promise<BoxProCheckIn> {
    try {
      const response = await api.post(`/box-pro-check-ins/bookings/${bookingId}`, {
        boxProId,
        requestNote: requestNote?.trim() || undefined,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message || 'Failed to request a Box Pro check-in'
      );
    }
  }

  /**
   * Cancel a booking
   */
  static async cancelBooking(bookingId: string): Promise<void> {
    try {
      await api.delete(`/bookings/${bookingId}`);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to cancel booking';
      throw new Error(message);
    }
  }

  /**
   * Check in to a booking
   */
  static async checkInBooking(bookingId: string): Promise<BookingDetailsDto> {
    try {
      const response = await api.post(`/bookings/${bookingId}/check-in`);
      return response.data;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to check in';
      throw new Error(message);
    }
  }

  static async getTodayUsage(
    facility: string,
    date: string,
    locationId: string,
    excludeBookingId?: string,
  ): Promise<{ minutesBooked: number; bookingCount: number }> {
    try {
      const response = await api.get('/bookings/today-usage', {
        params: { facility, date, locationId, ...(excludeBookingId ? { excludeBookingId } : {}) },
      });
      return response.data;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to get today usage';
      throw new Error(message);
    }
  }
}

export default BookingService;
